import { useEffect, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiCard, UiCardHeader, UiCardBody, UiBreadcrumb, UiDataTable } from 'components/common'
import { Box, Flex, Heading, Spinner, Text } from '@chakra-ui/react'
import { useDebouncedCallback } from 'use-debounce'
import { useAppContext } from 'hooks/useAppContext'
import { getMetricsNoActivity } from 'api'
import { format as formatDate } from 'date-fns'

const BreadcrumbNav = [
  {
    label: 'Metrics',
    path: '/reporting/metrics'
  },
  {
    label: 'Projects w/No Activity >7 days',
    isCurrentPage: true
  }
]

const TABLE_COLUMNS = [
  {
    label: 'ID',
    orderBy: 'id',
    render: ({ id }) => id
  },
  {
    label: 'Project',
    orderBy: 'customerCompany',
    render: ({ id, customerCompany, onView }) => <Text color="blue.400" cursor="pointer" onClick={() => onView(id)}>{customerCompany}</Text>
  },
  {
    label: 'Stage',
    orderBy: 'stage',
    render: ({ stage }) => stage
  },
  {
    label: 'Last Activity',
    orderBy: 'lastActivityDate',
    render: ({ lastActivityDate }) => lastActivityDate ? formatDate(new Date(lastActivityDate), 'MM/dd/yyyy') : 'N/A'
  }
]

export const MetricsNoActivity = () => {
  const { createToast } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 20,
    search: '',
    orderBy: {
      key: 'id',
      direction: 'asc'
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [data, setData] = useState([])

  const filter = () => {
    setIsLoading(true)
    const searchedAnalytics = data.filter(v => v.customerCompany.includes(tableConfig.search) || v.stage.includes(tableConfig.search))
    const sortedAnalytics = searchedAnalytics.sort((a, b) => {
      const direction = tableConfig.orderBy.direction === 'asc' ? 1 : -1
      return a[tableConfig.orderBy.key] < b[tableConfig.orderBy.key]
        ? 1 * direction
        : (a[tableConfig.orderBy.key] === b[tableConfig.orderBy.key] ? 0 : -1 * direction)
    })
    const analyticsForPage = sortedAnalytics.slice((tableConfig.curPage - 1) * tableConfig.itemsPerPage, tableConfig.curPage * tableConfig.itemsPerPage)
    setRows(analyticsForPage.map(v => ({ ...v, onView: () => onView(v.id) })))
    setIsLoading(false)
  }

  const debouncedFilter = useDebouncedCallback(filter, 400)

  useEffect(() => {
    debouncedFilter()
  }, [tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  useEffect(() => {
    filter()
    setTableConfig(draft => {
      draft.totalRecords = data.length || 0
      draft.totalPages = Math.ceil(draft.totalRecords / draft.itemsPerPage)
    })
  }, [data])

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getMetricsNoActivity()
      setData(response)
      setIsLoading(false)
    } catch (err) {
      createToast('error', 'Error', 'Failed to load data.')
      console.log(err)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  const onView = (projectId) => {
    window.open(`/projects/detail/${projectId}`, '_blank')
  }

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box my={8}>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">{'Projects w/No Activity >7 days'}</Heading>
        </Flex>
        <UiCard borderRadius={0}>
          <UiCardHeader bg="gray.500" p={4}>
            <Heading color="white" size="sm">Projects List</Heading>
          </UiCardHeader>
          <UiCardBody p={8}>
            {isLoading ? (
              <Flex alignItems="center" gap={4}>
                <Spinner color="blue.400" size="sm" />
                <Text fontSize="sm">Loading ...</Text>
              </Flex>
            ) : (
              <UiDataTable
                key="table-metrics-no-activity"
                tableConfig={tableConfig}
                updateTableConfig={setTableConfig}
                rows={rows}
                rowKey="id"
                columns={TABLE_COLUMNS}
                isLoading={isLoading}
              />
            )}
          </UiCardBody>
        </UiCard>
      </Box>
    </>
  )
}
